import { IKnmPmFormReason } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormReason';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { IKnmPmFormRequirementsNpa } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';

import { FormErrors } from '../../../KnmPackage/MainModule/types/FormErrors';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { PmInvalidFieldEnum } from '../../../KnmPackage/MainModule/enums/PmInvalidFieldEnum';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';
import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';

const INVALID_FIELD_ERROR = 'Сведения устарели в системе-источнике данных';

export const isInvalidField = (
  fieldTemplate: PmInvalidFieldEnum,
  invalidFields: string[],
  ...guidList: string[]
): boolean => {
  let field: string = fieldTemplate;

  if (fieldTemplate.includes('{id}') && guidList.length) {
    guidList.forEach(guid => {
      field = field.replace('{id}', guid);
    });
  }

  return invalidFields.includes(field);
};

const isInitialValue = (
  currentValue: IDictionarySelectOption<{}> | undefined,
  initialValue: IDictionarySelectOption<{}> | undefined,
): boolean => {
  return !!currentValue?.value.dictVersionId && currentValue.value.dictVersionId === initialValue?.value.dictVersionId;
};

const getReasonErvkUuid = (reason: IKnmPmFormReason | undefined): string | undefined | null => {
  return reason?.reason?.ervkUuid || reason?.type?.ervkUuid;
};

const getRequirementNpaId = (requirementsNpa: IKnmPmFormRequirementsNpa): string => {
  const npaId = requirementsNpa.npa.id;
  const structuralUnitId = requirementsNpa.structuralUnit.id;

  return `${npaId}|${structuralUnitId}`;
};

/*
  Валидация для подсветки полей с неактуальными справочными значениями
*/
export const validateInvalidFieldsPm = (
  values: IKnmForm,
  initialFormValues: IKnmPmForm | undefined,
  isNewKnm: boolean | undefined,
  invalidFields?: string[],
): FormErrors<IKnmForm> => {
  const { reasonsPm, supervisionTypePm, knoOrganizationPm, eventsPm, pmType, requirementsNpa } = values;

  if (!invalidFields || !initialFormValues) {
    return {};
  }

  const initialReasonsPm = initialFormValues.reasonsPm;
  const initialSupervisionTypePm = initialFormValues.supervisionTypePm;
  const initialKnoOrganizationPm = initialFormValues.knoOrganizationPm;
  const initialPmType = initialFormValues.pmType;
  const initialEventsPm = initialFormValues.eventsPm;

  const result = {
    reasonsPm: reasonsPm?.map(
      (item): FormErrors<IKnmPmFormReason> => {
        const initialItem: IKnmPmFormReason | undefined = initialReasonsPm?.find(
          initialReason => item.numGuid && item.numGuid === initialReason.numGuid,
        );
        const itemErvkUuid = getReasonErvkUuid(item);
        const initialItemErvkUuid = getReasonErvkUuid(initialItem);

        return {
          type:
            itemErvkUuid &&
            itemErvkUuid === initialItemErvkUuid &&
            isInvalidField(PmInvalidFieldEnum.PM_REASONS_ERKNM_ERVK_UUID, invalidFields, item.numGuid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
          assignmentNumber:
            !isNewKnm &&
            item.order?.recordVersionId &&
            item.order.recordVersionId === initialItem?.order?.recordVersionId &&
            isInvalidField(PmInvalidFieldEnum.PM_REASONS_ERKNM_ORDER, invalidFields, item.numGuid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
          riskIndicators: item.riskIndicators?.map(
            (riskIndicatorItem): FormErrors<IKnmFormRiskIndicators> => {
              const initialRiskIndicatorItem = initialItem?.riskIndicators?.find(
                initialRiskIndicator => riskIndicatorItem.guid && riskIndicatorItem.guid === initialRiskIndicator.guid,
              );

              return {
                riskIndicator:
                  !isNewKnm &&
                  isInitialValue(riskIndicatorItem.riskIndicator, initialRiskIndicatorItem?.riskIndicator) &&
                  (isInvalidField(
                    PmInvalidFieldEnum.PM_REASONS_ERKNM_RISK_INDICATORS,
                    invalidFields,
                    item.numGuid || '',
                  ) ||
                    isInvalidField(
                      PmInvalidFieldEnum.PM_REASONS_ERKNM_RISK_INDICATOR,
                      invalidFields,
                      item.numGuid || '',
                      riskIndicatorItem.guid,
                    ))
                    ? INVALID_FIELD_ERROR
                    : undefined,
              };
            },
          ),
        };
      },
    ),
    supervisionTypePm:
      isInitialValue(supervisionTypePm, initialSupervisionTypePm) &&
      isInvalidField(PmInvalidFieldEnum.PM_SUPERVISION_TYPE, invalidFields)
        ? INVALID_FIELD_ERROR
        : undefined,
    knoOrganizationPm:
      isInitialValue(knoOrganizationPm, initialKnoOrganizationPm) &&
      isInvalidField(PmInvalidFieldEnum.PM_KNO_ORGANIZATION, invalidFields)
        ? INVALID_FIELD_ERROR
        : undefined,
    pmType:
      isInitialValue(pmType, initialPmType) && isInvalidField(PmInvalidFieldEnum.PM_TYPE, invalidFields)
        ? INVALID_FIELD_ERROR
        : undefined,
    eventsPm: eventsPm?.map(
      (item): FormErrors<IKnmErknmFormEvent> => {
        const initialItem = initialEventsPm?.find(initialEvent => item.guid && item.guid === initialEvent.guid);

        return {
          type:
            isInitialValue(item.type, initialItem?.type) &&
            isInvalidField(PmInvalidFieldEnum.PM_EVENTS_ERKNM_TYPE, invalidFields, item.guid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
        };
      },
    ),
    requirementsNpa: requirementsNpa?.map((item): FormErrors<IKnmPmFormRequirementsNpa> | undefined =>
      isInvalidField(PmInvalidFieldEnum.PM_REQUIREMENTS_NPA_ID, invalidFields, getRequirementNpaId(item)) ||
      isInvalidField(
        PmInvalidFieldEnum.PM_REQUIREMENTS_NPA_STRUCTURAL_UNIT_ID,
        invalidFields,
        getRequirementNpaId(item),
      )
        ? { npa: INVALID_FIELD_ERROR }
        : {},
    ),
    subjectTypePm:
      supervisionTypePm?.code && isInvalidField(PmInvalidFieldEnum.PM_ORGANIZATIONS_SUBJECT_TYPE, invalidFields, '')
        ? INVALID_FIELD_ERROR
        : undefined,
  };

  return JSON.parse(JSON.stringify(result));
};
